// @types/webpack-env is not installed — declare require.context manually
declare const require: { context: (_directory: string, _useSubdirectories: boolean, _regExp: RegExp) => any };

import { registerResources, unregisterResources } from '~/plugins/ResourceRegistry';

type PluginModule = {
  initPlugin?: () => void;
  destroyPlugin?: () => void;
  resources_ja?: Record<string, string>;
  // parentPlugin removed — parent-child resolved via API pluginHierarchy (CONTEXT.md decision)
};

type LoadedPlugin = {
  pluginKey: string;
  module: PluginModule;
};

let _initialized = false;
const _loadedPlugins: LoadedPlugin[] = [];

const _loadModule = (
  pluginKey: string,
  mod: PluginModule,
  pluginHierarchy: Record<string, string[]>,
): void => {
  // Determine if this pluginKey is a subplugin of a known parent.
  // pluginKey formats: 'plugin:parent-name' (root), 'plugin:parent:child' (sub), 'module:parent:child' (sub)
  // pluginHierarchy keys are PascalCase flag keys (e.g. "MonthlyReportCustomization")
  // We check if any parent in the hierarchy lists this plugin's key segment.
  const isSubplugin = Object.values(pluginHierarchy).some((children) =>
    children.some((child) => pluginKey.endsWith(`:${child}`) || pluginKey.endsWith(`:${child.toLowerCase()}`)),
  );

  if (isSubplugin) {
    // Find the parent pluginKey in _loadedPlugins.
    const parentLoaded = Object.entries(pluginHierarchy).some(([parentFlagKey, children]) => {
      const hasThisChild = children.some(
        (child) => pluginKey.endsWith(`:${child}`) || pluginKey.endsWith(`:${child.toLowerCase()}`),
      );
      if (!hasThisChild) return false;

      // Check if parent is loaded — parent pluginKey is 'plugin:{kebab-case-name}' or 'module:{kebab-case-name}'
      return _loadedPlugins.some((p) =>
        p.pluginKey.includes(
          parentFlagKey
            .toLowerCase()
            .replace(/([a-z])([A-Z])/g, '$1-$2')
            .toLowerCase(),
        ),
      );
    });

    if (!parentLoaded) {
      console.warn(
        `[ClientPluginLoader] skipping ${pluginKey} because parent plugin is not loaded (pluginHierarchy-based check)`,
      );
      return;
    }
  }

  if (mod.resources_ja) {
    registerResources(pluginKey, mod.resources_ja);
  }

  mod.initPlugin?.();

  _loadedPlugins.push({ pluginKey, module: mod });
};

const _loadContext = (
  contextFn: () => ReturnType<typeof require.context>,
  keyTransform: (_key: string) => string,
  pluginHierarchy: Record<string, string[]>,
): void => {
  let ctx: ReturnType<typeof require.context>;
  try {
    ctx = contextFn();
  } catch {
    // context not available (e.g., test environment)
    return;
  }

  ctx.keys().forEach((key: string) => {
    const pluginKey = keyTransform(key);

    // Prevent duplicate loading
    if (_loadedPlugins.some((p) => p.pluginKey === pluginKey)) {
      return;
    }

    const mod: PluginModule = ctx(key);
    _loadModule(pluginKey, mod, pluginHierarchy);
  });
};

export const _loadModulesForTesting = (
  modules: Array<{ pluginKey: string; module: PluginModule }>,
  pluginHierarchy: Record<string, string[]> = {},
): void => {
  modules.forEach(({ pluginKey, module }) => {
    if (_loadedPlugins.some((p) => p.pluginKey === pluginKey)) {
      return;
    }

    _loadModule(pluginKey, module, pluginHierarchy);
  });
};

export const initializePlugins = (pluginHierarchy: Record<string, string[]> = {}): void => {
  if (_initialized) {
    console.warn('[ClientPluginLoader] initializePlugins() called more than once — skipping');
    return;
  }
  _initialized = true;

  // plugins/*/client/index.ts
  _loadContext(
    () => require.context('@plugins', true, /^\.\/[^/]+\/client\/index\.ts$/),
    (key) => key.replace(/^\.\/([^/]+)\/client\/index\.ts$/, 'plugin:$1'),
    pluginHierarchy,
  );

  // modules/*/client/index.ts
  _loadContext(
    () => require.context('@modules', true, /^\.\/[^/]+\/client\/index\.ts$/),
    (key) => key.replace(/^\.\/([^/]+)\/client\/index\.ts$/, 'module:$1'),
    pluginHierarchy,
  );

  // plugins/*/sub-plugins/*/client/index.ts
  _loadContext(
    () => require.context('@plugins', true, /^\.\/[^/]+\/sub-plugins\/[^/]+\/client\/index\.ts$/),
    (key) => key.replace(/^\.\/([^/]+)\/sub-plugins\/([^/]+)\/client\/index\.ts$/, 'plugin:$1:$2'),
    pluginHierarchy,
  );

  // modules/*/sub-plugins/*/client/index.ts
  _loadContext(
    () => require.context('@modules', true, /^\.\/[^/]+\/sub-plugins\/[^/]+\/client\/index\.ts$/),
    (key) => key.replace(/^\.\/([^/]+)\/sub-plugins\/([^/]+)\/client\/index\.ts$/, 'module:$1:$2'),
    pluginHierarchy,
  );
};

export const destroyPlugins = (): void => {
  // Destroy in reverse order
  [..._loadedPlugins].reverse().forEach(({ pluginKey, module: mod }) => {
    mod.destroyPlugin?.();
    unregisterResources(pluginKey);
  });

  _loadedPlugins.length = 0;
  _initialized = false;
};
